if (!customElements.get('tpt-blog-pagination')) {
  customElements.define(
    'tpt-blog-pagination',
    class TptBlogPagination extends HTMLElement {
      constructor() {
        super();
        this.currentPage = 1;
        this.slides = [];
        this.totalItems = 0;
        this.perPage = 12;
        this.totalPages = 1;
      }

      connectedCallback() {
        // Small delay to ensure DOM and siblings (swiper) are ready
        setTimeout(() => {
          this.init();
        }, 10);
      }

      init() {
        this.container = this.closest('.tpt-blog-post-1-container') || this.parentElement;
        if (!this.container) return;

        this.slides = Array.from(this.container.querySelectorAll('.swiper-wrapper > .swiper-slide'));
        this.totalItems = this.slides.length;
        this.perPage = parseInt(this.dataset.perPage, 10) || 12;
        this.paginationType = this.dataset.paginationType || 'numbers';
        this.loadMoreLabel = this.dataset.loadMoreLabel || 'Load more posts';
        this.currentPage = 1;
        this.totalPages = Math.ceil(this.totalItems / this.perPage);

        if (this.totalItems <= this.perPage || this.totalPages <= 1) {
          this.style.display = 'none';
          this.slides.forEach(slide => slide.classList.remove('tpt-slide-hidden'));
          return;
        }

        this.style.display = '';
        this.renderPagination();
        this.updatePage(1, false, false);
      }

      renderPagination() {
        this.innerHTML = '';
        const alignment = this.dataset.alignment || 'center';
        this.setAttribute('data-alignment', alignment);

        switch (this.paginationType) {
          case 'dots':
            this.renderDots();
            break;
          case 'load_more':
            this.renderLoadMore();
            break;
          case 'fraction':
            this.renderFraction();
            break;
          case 'numbers':
          default:
            this.renderNumbers();
            break;
        }
      }

      renderNumbers() {
        const wrapper = document.createElement('nav');
        wrapper.className = 'tpt-blog-pagination__numbers';
        wrapper.setAttribute('role', 'navigation');
        wrapper.setAttribute('aria-label', 'Blog pagination');

        // Prev button
        const prevBtn = document.createElement('button');
        prevBtn.type = 'button';
        prevBtn.className = 'tpt-blog-pagination__arrow tpt-blog-pagination__prev';
        prevBtn.setAttribute('aria-label', 'Previous page');
        prevBtn.innerHTML = `
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        `;
        prevBtn.addEventListener('click', () => {
          if (this.currentPage > 1) {
            this.goToPage(this.currentPage - 1);
          }
        });
        wrapper.appendChild(prevBtn);
        this.prevBtn = prevBtn;

        // Numbers container
        const numbersContainer = document.createElement('div');
        numbersContainer.className = 'tpt-blog-pagination__pages';
        wrapper.appendChild(numbersContainer);
        this.numbersContainer = numbersContainer;

        // Next button
        const nextBtn = document.createElement('button');
        nextBtn.type = 'button';
        nextBtn.className = 'tpt-blog-pagination__arrow tpt-blog-pagination__next';
        nextBtn.setAttribute('aria-label', 'Next page');
        nextBtn.innerHTML = `
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        `;
        nextBtn.addEventListener('click', () => {
          if (this.currentPage < this.totalPages) {
            this.goToPage(this.currentPage + 1);
          }
        });
        wrapper.appendChild(nextBtn);
        this.nextBtn = nextBtn;

        this.appendChild(wrapper);
        this.updateNumbersList();
      }

      updateNumbersList() {
        if (!this.numbersContainer) return;
        this.numbersContainer.innerHTML = '';

        const createPageBtn = (pageNum) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'tpt-blog-pagination__item' + (pageNum === this.currentPage ? ' is-active' : '');
          btn.textContent = pageNum;
          btn.setAttribute('aria-label', `Page ${pageNum}`);
          if (pageNum === this.currentPage) {
            btn.setAttribute('aria-current', 'page');
          }
          btn.addEventListener('click', () => this.goToPage(pageNum));
          return btn;
        };

        const createEllipsis = () => {
          const span = document.createElement('span');
          span.className = 'tpt-blog-pagination__ellipsis';
          span.setAttribute('aria-hidden', 'true');
          span.textContent = '...';
          return span;
        };

        if (this.totalPages <= 7) {
          for (let i = 1; i <= this.totalPages; i++) {
            this.numbersContainer.appendChild(createPageBtn(i));
          }
        } else {
          this.numbersContainer.appendChild(createPageBtn(1));

          if (this.currentPage > 3) {
            this.numbersContainer.appendChild(createEllipsis());
          }

          const start = Math.max(2, this.currentPage - 1);
          const end = Math.min(this.totalPages - 1, this.currentPage + 1);

          for (let i = start; i <= end; i++) {
            this.numbersContainer.appendChild(createPageBtn(i));
          }

          if (this.currentPage < this.totalPages - 2) {
            this.numbersContainer.appendChild(createEllipsis());
          }

          this.numbersContainer.appendChild(createPageBtn(this.totalPages));
        }

        if (this.prevBtn) this.prevBtn.disabled = this.currentPage === 1;
        if (this.nextBtn) this.nextBtn.disabled = this.currentPage === this.totalPages;
      }

      renderDots() {
        const wrapper = document.createElement('div');
        wrapper.className = 'tpt-blog-pagination__dots';
        wrapper.setAttribute('role', 'tablist');
        wrapper.setAttribute('aria-label', 'Blog pagination dots');

        this.dotButtons = [];
        for (let i = 1; i <= this.totalPages; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'tpt-blog-pagination__dot' + (i === 1 ? ' is-active' : '');
          dot.setAttribute('aria-label', `Page ${i}`);
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-selected', i === 1 ? 'true' : 'false');
          dot.addEventListener('click', () => this.goToPage(i));
          wrapper.appendChild(dot);
          this.dotButtons.push(dot);
        }
        this.appendChild(wrapper);
      }

      renderLoadMore() {
        const wrapper = document.createElement('div');
        wrapper.className = 'tpt-blog-pagination__load-more';

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tpt-blog-pagination__load-more-btn';
        btn.innerHTML = `
          <span>${this.loadMoreLabel}</span>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        `;
        btn.addEventListener('click', () => {
          if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.showItemsUpTo(this.currentPage * this.perPage);
            this.updateLoadMoreState();
          }
        });
        wrapper.appendChild(btn);
        this.loadMoreBtn = btn;

        const count = document.createElement('div');
        count.className = 'tpt-blog-pagination__count';
        wrapper.appendChild(count);
        this.loadMoreCount = count;

        this.appendChild(wrapper);
        this.updateLoadMoreState();
      }

      updateLoadMoreState() {
        const currentCount = Math.min(this.currentPage * this.perPage, this.totalItems);
        if (this.loadMoreCount) {
          this.loadMoreCount.textContent = `Showing ${currentCount} of ${this.totalItems} posts`;
        }
        if (this.loadMoreBtn) {
          if (currentCount >= this.totalItems) {
            this.loadMoreBtn.style.display = 'none';
          } else {
            this.loadMoreBtn.style.display = '';
          }
        }
      }

      renderFraction() {
        const wrapper = document.createElement('div');
        wrapper.className = 'tpt-blog-pagination__fraction';

        const prevBtn = document.createElement('button');
        prevBtn.type = 'button';
        prevBtn.className = 'tpt-blog-pagination__arrow tpt-blog-pagination__prev';
        prevBtn.setAttribute('aria-label', 'Previous page');
        prevBtn.innerHTML = `
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        `;
        prevBtn.addEventListener('click', () => {
          if (this.currentPage > 1) {
            this.goToPage(this.currentPage - 1);
          }
        });
        wrapper.appendChild(prevBtn);
        this.fractionPrevBtn = prevBtn;

        const text = document.createElement('span');
        text.className = 'tpt-blog-pagination__fraction-text';
        text.innerHTML = `Page <span class="current">${this.currentPage}</span> of <span class="total">${this.totalPages}</span>`;
        wrapper.appendChild(text);
        this.fractionText = text;

        const nextBtn = document.createElement('button');
        nextBtn.type = 'button';
        nextBtn.className = 'tpt-blog-pagination__arrow tpt-blog-pagination__next';
        nextBtn.setAttribute('aria-label', 'Next page');
        nextBtn.innerHTML = `
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        `;
        nextBtn.addEventListener('click', () => {
          if (this.currentPage < this.totalPages) {
            this.goToPage(this.currentPage + 1);
          }
        });
        wrapper.appendChild(nextBtn);
        this.fractionNextBtn = nextBtn;

        this.appendChild(wrapper);
        this.updateFractionState();
      }

      updateFractionState() {
        if (this.fractionText) {
          this.fractionText.innerHTML = `Page <span class="current">${this.currentPage}</span> of <span class="total">${this.totalPages}</span>`;
        }
        if (this.fractionPrevBtn) this.fractionPrevBtn.disabled = this.currentPage === 1;
        if (this.fractionNextBtn) this.fractionNextBtn.disabled = this.currentPage === this.totalPages;
      }

      goToPage(page) {
        if (page === this.currentPage && this.paginationType !== 'load_more') return;
        this.currentPage = page;
        this.updatePage(page, true, true);

        // Smooth scroll to top of section
        const sectionContainer = this.closest('.tpt-section_container') || this.container;
        if (sectionContainer) {
          const headerOffset = 100;
          const elementPosition = sectionContainer.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        }
      }

      updatePage(page, animate = false, isUserAction = false) {
        const start = (page - 1) * this.perPage;
        const end = page * this.perPage;

        this.slides.forEach((slide, index) => {
          if (index >= start && index < end) {
            slide.classList.remove('tpt-slide-hidden');
            if (animate) {
              slide.classList.remove('tpt-slide-animating');
              void slide.offsetWidth; // trigger reflow
              slide.classList.add('tpt-slide-animating');
            }
          } else {
            slide.classList.add('tpt-slide-hidden');
          }
        });

        // Update Swiper if present (e.g. mobile)
        const swiperEl = this.container.querySelector('.swiper');
        if (swiperEl && swiperEl.swiper) {
          swiperEl.swiper.update();
          if (isUserAction) {
            swiperEl.swiper.slideTo(0);
          }
        }

        // Update controls
        if (this.paginationType === 'numbers') {
          this.updateNumbersList();
        } else if (this.paginationType === 'dots') {
          if (this.dotButtons) {
            this.dotButtons.forEach((dot, idx) => {
              const isActive = idx + 1 === page;
              dot.classList.toggle('is-active', isActive);
              dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });
          }
        } else if (this.paginationType === 'fraction') {
          this.updateFractionState();
        }
      }

      showItemsUpTo(count) {
        this.slides.forEach((slide, index) => {
          if (index < count) {
            if (slide.classList.contains('tpt-slide-hidden')) {
              slide.classList.remove('tpt-slide-hidden');
              slide.classList.remove('tpt-slide-animating');
              void slide.offsetWidth;
              slide.classList.add('tpt-slide-animating');
            }
          } else {
            slide.classList.add('tpt-slide-hidden');
          }
        });

        const swiperEl = this.container.querySelector('.swiper');
        if (swiperEl && swiperEl.swiper) {
          swiperEl.swiper.update();
        }
      }
    }
  );

  // Shopify Theme Editor listener
  document.addEventListener('shopify:section:load', (event) => {
    const pagination = event.target.querySelector('tpt-blog-pagination');
    if (pagination && typeof pagination.init === 'function') {
      pagination.init();
    }
  });
}
